import React, { useState, useEffect, useRef } from 'react';
import { useVapi } from '../../contexts/VoiceCallContext';
import { useAudioRecovery, markCallManuallyEnded } from '../../hooks/useAudioRecovery';
import useWakeLock from '../../hooks/useWakeLock';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import PushToTalkButton from './PushToTalkButton';
import PrivacyPolicyModal from '../common/PrivacyPolicyModal';
import toast from 'react-hot-toast';
import { defaultAssistantId } from '../../lib/vapi';
import { hasAcceptedTerms } from '../../services/termsService';
const { FiPhone, FiPhoneOff, FiMic, FiMicOff, FiVolume2, FiVolumeX, FiX, FiRefreshCw, FiEye, FiEyeOff } = FiIcons;

const VoiceCallWidget = ({ selectedAssistant, isFloating = true, hideAssistantInfo = false }) => {
  const {
    isCallActive,
    isSpeaking,
    callStatus,
    transcript,
    chatMessages,
    callError,
    startCall,
    endCall,
    setMuted,
    isAssistantMuted,
    setAssistantMuted
  } = useVapi();
  
  const { isSupported: wakeLockSupported, isActive: wakeLockActive, requestWakeLock, releaseWakeLock } = useWakeLock();
  
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isWakeLockEnabled, setIsWakeLockEnabled] = useState(false);
  const transcriptEndRef = useRef(null);
  const floatingChatRef = useRef(null);
  const lastAssistantRef = useRef(null); // ✅ Remember last used assistant
  const [isUserScrolled, setIsUserScrolled] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(null); // null = not checked yet, true/false = checked
  const pendingCallRef = useRef(false); // Track if call should start after terms acceptance

  // Check if user has accepted terms on component mount
  useEffect(() => {
    const checkTermsAcceptance = async () => {
      try {
        const { accepted } = await hasAcceptedTerms();
        setTermsAccepted(accepted);
      } catch (error) {
        console.error('Error checking terms acceptance:', error);
        // Default to requiring acceptance if there's an error
        setTermsAccepted(false);
      }
    };

    checkTermsAcceptance();
  }, []);

  // Handle when user accepts terms
  const handleTermsAccepted = React.useCallback(() => {
    setTermsAccepted(true);
    setShowPrivacyModal(false);
    
    // If there was a pending call, start it now
    if (pendingCallRef.current) {
      pendingCallRef.current = false;
      // Use setTimeout to ensure modal is fully closed before starting call
      setTimeout(() => {
        actuallyStartCall();
      }, 100);
    }
  }, []);

  // The actual call start logic (internal)
  const actuallyStartCall = React.useCallback(async () => {
    markCallManuallyEnded(false); // allow recovery again

    window._vapiHasEverStartedCall = true;
    console.log('🎯 Starting call from widget...');
    console.log('📋 Selected assistant:', selectedAssistant);
    
    try {
      if (selectedAssistant) {
        // Explicitly selected assistant (from Assistant page)
        console.log('🎯 Using explicitly selected assistant:', selectedAssistant.name);
        await startCall(null, selectedAssistant);
        lastAssistantRef.current = selectedAssistant;
      } else {
        // No selected assistant - let VapiContext handle assistant lookup
        // This will: 1) Load user's assigned assistant from DB, 2) Fall back to default assistant, 3) Fall back to env ID
        console.log('🎯 No selected assistant - VapiContext will auto-load user assigned assistant');
        await startCall(); // Call with NO parameters to trigger auto-load logic
        lastAssistantRef.current = null;
      }
      
      if (isFloating) {
        setIsExpanded(true);
      }
    } catch (error) {
      console.error('Error starting call:', error);
    }
  }, [selectedAssistant, startCall, isFloating]);

  // Public-facing start call function (checks terms first)
  const handleStartCall = React.useCallback(async () => {
    // Check if we've determined terms acceptance status yet
    if (termsAccepted === null) {
      // Still loading, show a loading message
      toast.loading('Checking account status...', { duration: 1000 });
      return;
    }

    // If terms not accepted, show the modal
    if (!termsAccepted) {
      console.log('📋 User has not accepted terms, showing modal...');
      pendingCallRef.current = true;
      setShowPrivacyModal(true);
      return;
    }

    // Terms already accepted, proceed with call
    await actuallyStartCall();
  }, [termsAccepted, actuallyStartCall]);

  // ✅ Safe, debounced reinit logic for audio recovery
  const reinitVoiceAgent = React.useCallback(async (kickAudioSystem) => {
    try {
      // Ignore reinit if call is just connecting (device change is likely due to mic activation)
      if (callStatus === 'connecting') {
        console.log("[AudioRecovery] Call is connecting — ignoring device change to avoid interference.");
        return;
      }

      if (!isCallActive) {
        console.log("[AudioRecovery] No active call — skipping reinit.");
        return;
      }
      console.log("[AudioRecovery] Attempting voice agent reinit...");

      // Skip if user manually ended the call
      if (window._vapiCallManuallyEnded) {
        console.log("[AudioRecovery] Call manually ended — skipping auto reinit.");
        return;
      }

      // Prevent multiple overlapping restarts
      if (window._vapiReinitInProgress) {
        console.log("[AudioRecovery] Reinit already in progress — skipping duplicate.");
        return;
      }

      window._vapiReinitInProgress = true;

      console.log("[AudioRecovery] Ending current call before restart...");
      await endCall();

      if (kickAudioSystem) {
        await kickAudioSystem();
      }

      console.log("[AudioRecovery] Restarting call...");
      await handleStartCall(); // ✅ Works with your no-param version

      console.log("[AudioRecovery] Voice agent successfully restarted.");
    } catch (err) {
      console.error("[AudioRecovery] Error reinitializing voice agent:", err);
    } finally {
      // Cooldown to prevent repeated triggers
      setTimeout(() => (window._vapiReinitInProgress = false), 1500);
    }
  }, [isCallActive, endCall, handleStartCall]);

  useAudioRecovery(reinitVoiceAgent);

  useEffect(() => {
    if (callError) {
      const msg = (callError.message || '').toLowerCase();
      const graceful =
        msg.includes('assistant-ended-call') ||
        msg.includes('customer-ended-call') ||
        msg.includes('meeting has ended') ||
        msg.includes('ended due to ejection') ||
        callStatus === 'idle'; // context already reset to idle

      if (graceful) {
        console.log('[Widget] Suppressing toast for graceful end.');
        return;
      }
      console.log('Contact error:', callError);
      toast.error(callError.message || 'Call failed');
    }
  }, [callError]);

  useEffect(() => {
    if (!isCallActive) {
      setIsMuted(false);
    }
  }, [isCallActive]);

  // Automatically enable wake lock when call starts
  useEffect(() => {
    if (isCallActive) {
      setIsWakeLockEnabled(true);
    } else {
      setIsWakeLockEnabled(false);
    }
  }, [isCallActive]);

  // Auto-scroll only if user hasn't manually scrolled up
  useEffect(() => {
    const scrollToBottom = (element) => {
      if (!element) return;
      
      // Only auto-scroll if user hasn't manually scrolled up
      if (!isUserScrolled) {
        element.scrollTop = element.scrollHeight;
      }
    };

    // Scroll inline mode chat
    scrollToBottom(transcriptEndRef.current);
    
    // Scroll floating widget chat
    scrollToBottom(floatingChatRef.current);
  }, [transcript, chatMessages, isUserScrolled]);

  // Detect manual scrolling
  useEffect(() => {
    const handleScroll = (element) => {
      if (!element) return;
      
      const { scrollTop, scrollHeight, clientHeight } = element;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 50; // 50px threshold
      
      // If user scrolls away from bottom, mark as manually scrolled
      if (!isAtBottom) {
        setIsUserScrolled(true);
      } else {
        // If user scrolls back to bottom, resume auto-scroll
        setIsUserScrolled(false);
      }
    };

    const inlineElement = transcriptEndRef.current;
    const floatingElement = floatingChatRef.current;

    const inlineScrollHandler = () => handleScroll(inlineElement);
    const floatingScrollHandler = () => handleScroll(floatingElement);

    if (inlineElement) {
      inlineElement.addEventListener('scroll', inlineScrollHandler);
    }
    
    if (floatingElement) {
      floatingElement.addEventListener('scroll', floatingScrollHandler);
    }

    return () => {
      if (inlineElement) {
        inlineElement.removeEventListener('scroll', inlineScrollHandler);
      }
      if (floatingElement) {
        floatingElement.removeEventListener('scroll', floatingScrollHandler);
      }
    };
  }, [chatMessages.length]); // Re-attach when messages change to ensure refs are valid

  // Wake lock management - prevent screen timeout during calls
  useEffect(() => {
    const manageWakeLock = async () => {
      if (isCallActive && isWakeLockEnabled && !wakeLockActive) {
        // Call is active AND wake lock enabled but not active - request wake lock
        console.log('Requesting wake lock (manual toggle)');
        const success = await requestWakeLock();
        
        if (!success && !wakeLockSupported) {
          // Show fallback notification for unsupported browsers
          toast('Keep your screen on manually during the call', {
            id: 'wake-lock-unsupported',
            icon: '📱',
            duration: 3000,
            position: 'bottom-center',
            style: {
              background: '#FEF3C7',
              color: '#92400E',
              border: '1px solid #F59E0B'
            }
          });
        }
      } else if (!isCallActive || !isWakeLockEnabled) {
        // Call ended OR wake lock disabled manually - release wake lock
        // We don't check wakeLockActive here to be safe, relying on releaseWakeLock to handle null check
        console.log('Releasing wake lock');
        await releaseWakeLock();
      }
    };

    manageWakeLock();
  }, [isCallActive, wakeLockActive, requestWakeLock, releaseWakeLock, wakeLockSupported, isWakeLockEnabled]);

  const handleWakeLockToggle = () => {
    const newState = !isWakeLockEnabled;
    setIsWakeLockEnabled(newState);
    if (newState) {
      toast.success('Screen will stay awake', { position: 'bottom-center', duration: 4000, id: 'wake-lock-toggle' });
    } else {
      toast('Screen timeout enabled', { icon: '🌙', position: 'bottom-center', duration: 4000, id: 'wake-lock-toggle' });
    }
  };

  const handleEndCall = async () => {
    try {
      window._vapiCallManuallyEnded = true; // flag for recovery system
      markCallManuallyEnded(true); // prevent auto-restart
      await endCall();
      if (isFloating) {
        setIsExpanded(false);
      }
    } catch (error) {
      console.error('Error ending call:', error);
    }
  };

  const handleMuteToggle = () => {
    const newMutedState = !isMuted;
    setMuted(newMutedState);
    setIsMuted(newMutedState);
    
    if (newMutedState) {
      toast('Microphone muted', {
        icon: '🔇',
        duration: 4000,
        position: 'bottom-center',
        id: 'mic-mute-toggle'
      });
    } else {
      toast('Microphone unmuted', {
        icon: '🎤',
        duration: 4000,
        position: 'bottom-center',
        id: 'mic-mute-toggle'
      });
    }
  };

  const handleAssistantMuteToggle = () => {
    const newAssistantMutedState = !isAssistantMuted;
    setAssistantMuted(newAssistantMutedState);

    if (newAssistantMutedState) {
      toast('Speaker disabled', {
        icon: '🔇',
        duration: 4000,
        position: 'bottom-center',
        id: 'assistant-mute-toggle'
      });
    } else {
      toast('Speaker enabled', {
        icon: '🔊',
        duration: 4000,
        position: 'bottom-center',
        id: 'assistant-mute-toggle'
      });
    }
  };

  const handleReset = () => {
    if (window.confirm("This will reload the app to fix audio issues. Continue?")) {
      window.location.reload();
    }
  };

  // Floating widget mode
  if (isFloating) {
    return (
      <div className="fixed right-6 z-50" style={{ bottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }}>
        <AnimatePresence>
          {isCallActive && isExpanded && (
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.9 }}
              className="mb-3 bg-white rounded-2xl shadow-2xl border border-gray-200 p-3 w-80"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiMic} className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {selectedAssistant?.name || 'Voice Assistant'}
                    </h3>
                    <p className="text-sm text-gray-500 capitalize">{callStatus}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                 {isCallActive && (
                   <>
                     <button
                       onClick={handleWakeLockToggle}
                       className={`p-2 rounded-full hover:bg-gray-100 transition-colors ${isWakeLockEnabled ? 'text-blue-600' : 'text-gray-400'}`}
                       title={isWakeLockEnabled ? "Disable Keep Awake" : "Enable Keep Awake"}
                     >
                       <SafeIcon icon={isWakeLockEnabled ? FiEye : FiEyeOff} className="w-6 h-6" />
                     </button>
                     <button
                       onClick={handleMuteToggle}
                       className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                     >
                       <SafeIcon icon={isMuted ? FiMicOff : FiMic} className="w-8 h-8 text-gray-600" />
                     </button>
                   </>
                 )}
                 <button
                   onClick={handleReset}
                   className="p-2 rounded-full hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
                   title="Reset Audio"
                 >
                   <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
                 </button>
                 <button
                   onClick={() => setIsExpanded(false)}
                   className="text-gray-400 hover:text-gray-600 p-1"
                 >
                   <SafeIcon icon={FiX} className="w-4 h-4" />
                 </button>
               </div>
              </div>

              {/* Chat Messages */}
              {chatMessages.length > 0 && (
                <div ref={floatingChatRef} className="mb-3 max-h-80 overflow-y-auto space-y-2">
                  {chatMessages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.speaker === 'assistant' ? 'justify-start' : 'justify-end'}`}
                    >
                      <div
                        className={`relative max-w-xs px-4 py-2 rounded-2xl text-sm ${
                          message.speaker === 'assistant'
                            ? 'bg-[#e5e5ea] text-gray-800 rounded-bl-none ml-3 before:content-[""] before:absolute before:left-[-8px] before:bottom-0 before:w-0 before:h-0 before:border-r-[12px] before:border-r-[#e5e5ea] before:border-t-[12px] before:border-t-transparent'
                            : 'bg-[#067ffe] text-white rounded-br-none mr-3 after:content-[""] after:absolute after:right-[-8px] after:bottom-0 after:w-0 after:h-0 after:border-l-[12px] after:border-l-[#067ffe] after:border-t-[12px] after:border-t-transparent'
                        }`}
                      >
                        {message.text}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Error display */}
              {callError && (
                <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{callError.message}</p>
                </div>
              )}

              <button
                onClick={handleEndCall}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors"
              >
                <SafeIcon icon={FiPhoneOff} className="w-4 h-4" />
                End Contact
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main call button */}
        <motion.button
          onClick={isCallActive ? (isExpanded ? handleEndCall : () => setIsExpanded(true)) : handleStartCall}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className={`w-16 h-16 rounded-full shadow-2xl flex items-center justify-center transition-colors ${
            isCallActive
              ? 'bg-red-600 hover:bg-red-700 text-white'
              : callStatus === 'connecting'
              ? 'bg-yellow-400 hover:bg-yellow-500 text-white'
              : 'bg-green-600 hover:bg-green-700 text-white'
          }`}
        >
          {isCallActive ? (
            <SafeIcon icon={isExpanded ? FiPhoneOff : FiPhone} className="w-6 h-6" />
          ) : (
            <SafeIcon icon={FiPhone} className="w-6 h-6" />
          )}
        </motion.button>

        {/* Call status indicator */}
        {isCallActive && !isExpanded && (
          <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
            <div className="w-3 h-3 bg-white rounded-full animate-pulse" />
          </div>
        )}
      </div>
    );
  }

  // Inline mode for dashboard
  return (
    <div className="bg-white rounded-xl shadow-lg p-3">
      {/* Assistant info */}
      {!hideAssistantInfo && selectedAssistant && (
        <div className="mb-3 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiMic} className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900">{selectedAssistant.name}</h4>
              <p className="text-sm text-gray-500">
                {selectedAssistant.voice?.name || selectedAssistant.voice || 'Default Voice'}
              </p>
            </div>
          </div>
        </div>
      )}


      {/* Error display */}
      {callError && (
        <div className="mb-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <h4 className="text-sm font-medium text-red-800 mb-1">Contact Error</h4>
          <p className="text-sm text-red-700">{callError.message}</p>
        </div>
      )}

      {/* Call controls */}
      <div className="flex items-center w-full">
        <AnimatePresence>
          {isCallActive && (
            <motion.div
              initial={{ width: 0, opacity: 0, marginRight: 0 }}
              animate={{ width: 'auto', opacity: 1, marginRight: 6 }}
              exit={{ width: 0, opacity: 0, marginRight: 0 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              className="flex items-center gap-1 overflow-hidden whitespace-nowrap"
            >
              {/* WakeLock Button */}
              <button
                onClick={handleWakeLockToggle}
                className={`w-14 h-14 flex-shrink-0 rounded-2xl flex items-center justify-center transition-all shadow-sm border-b-4 active:scale-95 ${
                  isWakeLockEnabled
                    ? 'bg-green-100 text-green-600 border-green-200'
                    : 'bg-gray-100 text-gray-400 border-gray-200'
                }`}
                title={isWakeLockEnabled ? "Disable Keep Awake" : "Enable Keep Awake"}
              >
                <SafeIcon icon={isWakeLockEnabled ? FiEye : FiEyeOff} className="w-6 h-6" />
              </button>

              {/* Assistant Mute Button */}
              <button
                onClick={handleAssistantMuteToggle}
                className={`w-14 h-14 flex-shrink-0 rounded-2xl flex items-center justify-center transition-all shadow-sm border-b-4 active:scale-95 ${
                  isAssistantMuted
                    ? 'bg-red-100 text-red-600 border-red-200'
                    : 'bg-blue-100 text-blue-600 border-blue-200'
                }`}
                title={isAssistantMuted ? "Unmute Assistant" : "Mute Assistant"}
              >
                <SafeIcon icon={isAssistantMuted ? FiVolumeX : FiVolume2} className="w-6 h-6" />
              </button>

              {/* Mic Mute Button */}
              <button
                onClick={handleMuteToggle}
                className={`w-14 h-14 flex-shrink-0 rounded-2xl flex items-center justify-center transition-all shadow-sm border-b-4 active:scale-95 ${
                  isMuted
                    ? 'bg-red-100 text-red-600 border-red-200'
                    : 'bg-gray-100 text-gray-600 border-gray-200'
                }`}
                title={isMuted ? "Unmute Mic" : "Mute Mic"}
              >
                <SafeIcon icon={isMuted ? FiMicOff : FiMic} className="w-6 h-6" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex-grow min-w-0">
          <PushToTalkButton
            callStatus={isCallActive ? 'active' : (callStatus === 'connecting' ? 'connecting' : 'idle')}
            onStart={handleStartCall}
            onStop={handleEndCall}
            width="100%"
            height={56}
          />
        </div>
      </div>

      {/* Chat Interface - Below the call controls */}
      {chatMessages.length > 0 && (
        <div className="mt-3">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Conversation</h4>
          <div
            ref={transcriptEndRef}
            className="bg-gray-50 rounded-lg p-4 h-[calc(100vh-280px)] min-h-[300px] overflow-y-auto space-y-3"
          >
            {chatMessages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.speaker === 'assistant' ? 'justify-start' : 'justify-end'}`}
              >
                <div className="flex flex-col max-w-xs">
                  <div
                    className={`relative px-4 py-2 rounded-2xl text-sm ${
                      message.speaker === 'assistant'
                        ? 'bg-[#e5e5ea] text-gray-800 rounded-bl-none ml-3 before:content-[""] before:absolute before:left-[-8px] before:bottom-0 before:w-0 before:h-0 before:border-r-[12px] before:border-r-[#e5e5ea] before:border-t-[12px] before:border-t-transparent'
                        : 'bg-[#067ffe] text-white rounded-br-none mr-3 after:content-[""] after:absolute after:right-[-8px] after:bottom-0 after:w-0 after:h-0 after:border-l-[12px] after:border-l-[#067ffe] after:border-t-[12px] after:border-t-transparent'
                    }`}
                  >
                    {message.text}
                  </div>
                  <span className={`text-xs text-gray-500 mt-1 ${
                    message.speaker === 'assistant' ? 'self-start' : 'self-end'
                  }`}>
                    {message.speaker === 'assistant' ? 'Assistant' : 'You'} • {message.timestamp.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Privacy Policy Modal */}
      <PrivacyPolicyModal
        isOpen={showPrivacyModal}
        onClose={() => {
          setShowPrivacyModal(false);
          pendingCallRef.current = false;
        }}
        onAccept={handleTermsAccepted}
      />
    </div>
  );
};

export default VoiceCallWidget;